import React from "react";
import { DownloadButton } from "@/global/base/download-button";
import { RippleHoverButton } from "../../../global/RippleHoverButton/RippleHoverButton";
import { ReactComponent as Valid } from "../../../../assets/icons/valid.svg";
import { ReactComponent as ShortGreenArrow } from "../../../../assets/icons/shortGreenArrow.svg";
import { ReactComponent as RevertApprover } from "../../../../assets/icons/revertApprover.svg";
import { ReactComponent as Rejected } from "../../../../assets/icons/rejected.svg";
import { canShowPrescriptionReviewAction } from "../../../../services/prescriptionReview";
import { canShowPrescriptionEditAction } from "../../../../services/prescriptionEdit";

/**
 * Prescriber/nurse renewal strip + pharmacy approve / reject / dispense / finish row.
 */
export function PafFooterActions({
  isPrescriber,
  isConnectedNurse,
  findProduct,
  isLastFormApprovedAvailableForRenew,
  getOpenedItem,
  isFormApproved,
  isHomecare,
  isOutpatient,
  isPharmacy,
  activePafDetails,
  onNavigateRenewalPaf,
  onApprove,
  onApproveAndDispense,
  onReject,
  onRevertToApprover,
  onDispense,
  onNavigateFinish,
  onDownloadPaf,
  onReview,
  onEdit,
  canReviewPaf,
  canChangePrescription,
  showEditAction,
  isEditMode,
  onSaveEdit,
  onCancelEdit,
  isSavingEdit,
  editSaveError,
}) {
  const openedItem = getOpenedItem?.() ?? {};
  const showReviewAction = canShowPrescriptionReviewAction({
    canReviewPaf,
    prescription: activePafDetails ?? openedItem,
    isFormApproved,
  });

  const showEditActionButton = showEditAction ?? canShowPrescriptionEditAction({
    canChangePrescription,
    prescription: activePafDetails ?? openedItem,
    isEditMode,
  });

  const reviewButtonClassName =
    "inline-flex h-[48px] min-w-[121px] items-center justify-center rounded-[33px] bg-[#d4eaf8] px-4 text-sm font-medium text-[#0e86d4] transition-colors hover:bg-[#0e86d4] hover:text-white disabled:cursor-not-allowed disabled:opacity-50";

  const reviewAndEditButtons = isEditMode ? (
    <>
      {editSaveError ? (
        <p
          className="w-full text-center text-sm text-red-600 sm:text-right"
          data-testid="paf-edit-save-error"
        >
          {editSaveError}
        </p>
      ) : null}
      <button
        type="button"
        className="h-[48px] bg-transparent px-3 text-sm font-semibold text-[#546e7a] underline underline-offset-2 hover:text-[#37474f] disabled:opacity-50"
        disabled={isSavingEdit}
        onClick={onCancelEdit}
        data-testid="paf-cancel-button"
      >
        Cancel changes
      </button>
      <RippleHoverButton
        variant="primary"
        className="h-[48px]"
        disabled={isSavingEdit}
        onClick={onSaveEdit}
        data-testid="paf-save-button"
      >
        {isSavingEdit ? "Saving…" : "Save changes"}
      </RippleHoverButton>
    </>
  ) : (
    <>
      {showEditActionButton && onEdit && (
        <RippleHoverButton
          variant="success"
          className="h-[48px]"
          onClick={onEdit}
          data-testid="paf-edit-button"
        >
          Edit
        </RippleHoverButton>
      )}
      {showReviewAction && onReview && (
        <button
          type="button"
          className={reviewButtonClassName}
          onClick={(event) => {
            event.stopPropagation();
            onReview();
          }}
          data-testid="paf-review-button"
        >
          Review
        </button>
      )}
    </>
  );

  const roleSpecificActions = isEditMode ? null :
    isPrescriber || isConnectedNurse ? (
      !isConnectedNurse &&
      findProduct?.can_renew_registration === true &&
      findProduct?.product_active === true && (
        <div className="renew-button" data-testid="paf-renewal-actions">
          {!(!isLastFormApprovedAvailableForRenew && isPrescriber) && (
            <>
              <button
                className="second-button"
                onClick={onNavigateRenewalPaf}
                data-testid="paf-renewal-button"
              >
                Renewal PAF
              </button>
              <div className="renewal-available">Renewal Available</div>
            </>
          )}
        </div>
      )
    ) : getOpenedItem()?.status === "Rejected" && isFormApproved === null ? (
      <div></div>
    ) : (getOpenedItem()?.status === "Overdue" ||
      getOpenedItem().status === "Expiring" ||
      getOpenedItem().status === "Approved" ||
      getOpenedItem().status === "Status Update" ||
      getOpenedItem()?.status === "Submitted") &&
      isFormApproved === null ? (
      <div
        className="flex w-full flex-wrap items-center justify-end gap-2 sm:gap-3 md:gap-5"
        data-testid="paf-pharmacy-actions"
      >
        {!isHomecare &&
          findProduct?.can_renew_registration === true &&
          findProduct?.product_active === true &&
          !isOutpatient &&
          getOpenedItem()?.status !== "Approved" && (
            <React.Fragment>
              <RippleHoverButton
                variant="primary"
                className="h-[48px]"
                onClick={onApprove}
                data-testid="paf-approve-button"
              >
                Approve
              </RippleHoverButton>
              <RippleHoverButton
                variant="success"
                className="h-[48px]"
                onClick={onApproveAndDispense}
                data-testid="paf-approve-dispense-button"
              >
                Approve & Dispense
              </RippleHoverButton>
              <RippleHoverButton
                variant="danger"
                className="h-[48px]"
                onClick={onReject}
                data-testid="paf-reject-button"
              >
                Reject
              </RippleHoverButton>
            </React.Fragment>
          )}

        {findProduct?.can_renew_registration === true &&
          findProduct?.product_active === true &&
          (isPharmacy || isHomecare || isOutpatient) &&
          !activePafDetails?.approved_or_rejected_by?.includes(
            "Awaiting approval"
          ) &&
          ["Approved", "Overdue", "Expiring", "Status Update"].includes(
            getOpenedItem().status
          ) && (
            <button
              className="revert-approver-button btn-paf mr-4"
              onClick={onRevertToApprover}
              data-testid="paf-revert-approver-button"
            >
              <span>Revert To Approver</span>
              <RevertApprover />
            </button>
          )}

        {(isHomecare || isOutpatient) &&
          findProduct?.can_renew_registration === true &&
          findProduct?.product_active === true &&
          ["Approved", "Overdue", "Expiring", "Status Update"].includes(
            getOpenedItem().status
          ) && (
            <button
              className="approve-button btn-paf mr-4"
              onClick={onDispense}
              data-testid="paf-dispense-button"
            >
              <span>Dispense</span>
              <ShortGreenArrow />
            </button>
          )}
      </div>
    ) : [
      "Approved",
      "Status Update",
      "Overdue",
      "Expiring",
      "Submitted",
      "Rejected",
    ].includes(getOpenedItem().status) && isFormApproved !== null ? (
      <React.Fragment>
        <div
          className="left-button"
          onClick={onNavigateFinish}
          data-testid="paf-finish-button"
          role="button"
        >
          Finish
        </div>
        {isFormApproved === true ? <Valid /> : <Rejected />}
      </React.Fragment>
    ) : (
      <div></div>
    );

  return (
    <div
      className="flex w-full flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3"
      data-testid="paf-footer-actions"
    >
      {onDownloadPaf && (
        <div className="w-full shrink-0 sm:w-auto sm:max-w-none">
          <DownloadButton
            className={`max-w-full ${isPharmacy || isHomecare || isOutpatient ? "h-[48px] text-sm" : ""}`}
            defaultWidth={121}
            aria-label="Download PAF"
            data-testid="paf-download-button"
            onDownload={(e) => {
              e.stopPropagation();
              return onDownloadPaf();
            }}
          />
        </div>
      )}
      <div
        className="flex min-w-0 w-full flex-1 flex-wrap items-center justify-center gap-2 sm:justify-end"
        data-testid="paf-footer-primary-actions"
      >
        {reviewAndEditButtons}
        {roleSpecificActions}
      </div>
    </div>
  );
}
